/**
 * MobileHeader — the phone frame's top bar, on every page.
 *
 * Deep routes: Back + title. Top-level tab destinations (Home, Tasks, Cycles,
 * Search) own their headings, so the bar shows only the mark there. Either way
 * the right end carries the desktop top bar's utilities (MobileHeaderActions):
 * theme, Guides and the notification bell (audit 2026-11-02, #35).
 */
import { ChevronLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveTitle } from './navConfig';
import { mobileBackTarget } from './mobileRouteCoverage';
import { MobileHeaderActions } from './MobileHeaderActions';
import { PrioritiesLogo } from '../components/ui/PrioritiesLogo';

interface MobileHeaderProps {
  /** A top-level tab destination: no Back and no title (the page has its own heading). */
  topLevel?: boolean;
}

export function MobileHeader({ topLevel = false }: MobileHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const title = resolveTitle(location.pathname);
  // A framed page's Back opens its parent (settings group, Strategy hub, the
  // cycle); the frame draws no second back link (audit #38).
  const backTarget = mobileBackTarget(location.pathname);

  return (
    <header className="pa-pt-safe fixed top-0 inset-x-0 z-40 bg-white/90 dark:bg-[color-mix(in_srgb,var(--bg-raised)_90%,transparent)] backdrop-blur-md border-b border-gray-200 dark:border-[var(--rule)]">
      <div className="h-11 px-2 flex items-center gap-1">
        {topLevel ? (
          <span className="h-9 px-1.5 inline-flex items-center flex-shrink-0" aria-hidden>
            <PrioritiesLogo height={22} markOnly />
          </span>
        ) : (
          <button
            type="button"
            onClick={() => (backTarget ? navigate(backTarget.href) : navigate(-1))}
            aria-label={backTarget ? `Back to ${backTarget.label}` : 'Back'}
            title={backTarget ? `Back to ${backTarget.label}` : undefined}
            className="h-9 w-9 flex-shrink-0 inline-flex items-center justify-center rounded-full text-gray-600 dark:text-[var(--text-sec)] active:bg-gray-100 dark:active:bg-[var(--hover-tint)]"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {topLevel ? (
          <div className="flex-1" />
        ) : (
          <h1 className="flex-1 min-w-0 font-fieldwork text-sm font-semibold text-gray-900 dark:text-[var(--text-pri)] truncate px-1">
            {title}
          </h1>
        )}
        <MobileHeaderActions guides />
      </div>
    </header>
  );
}
